import React, { useState, useCallback } from 'react';
import { ChevronUp, ChevronDown, ChevronRight } from 'lucide-react';
import { Skeleton } from './Skeleton';
import { EmptyState } from './EmptyState';
import { ErrorState } from './ErrorState';
import './DataTable.css';

export function DataTable({
  columns = [],
  data = [],
  loading = false,
  // Ошибка загрузки: без неё упавший запрос выглядел как «Нет данных»
  error = null,
  onRetry,
  emptyIcon,
  emptyTitle = 'Нет данных',
  emptyDescription,
  emptyAction,
  sortKey,
  sortDir,
  onSort,
  onRowClick,
  stickyHeader = true,
  stickyFirstColumn = false,
  zebraStripe = true,
  density = 'default',
  selectable = false,
  selectedIds = new Set(),
  onSelectionChange,
  isRowSelectable = () => true,
  rowId = (row) => row.id,
  rowClassName,
  footer,
  className = '',
  // Группировка: (row) => {key, label} и порядок ключей. Строка-заголовок группы
  // рисуется внутри одной таблицы — шапка со столбцами остаётся единственной.
  groupBy,
  groupOrder,
}) {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = useCallback((idx) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  }, []);

  const priorityCols = columns.filter(c => c.priority);
  const extraCols = columns.filter(c => !c.priority);

  const handleSort = (col) => {
    if (!col.sortable || !onSort) return;
    const dir = sortKey === col.key && sortDir === 'asc' ? 'desc' : 'asc';
    onSort(col.key, dir);
  };

  const selectableRows = data.filter(row => isRowSelectable(row));
  const allSelected = selectableRows.length > 0 && selectableRows.every(row => selectedIds.has(rowId(row)));
  const someSelected = selectableRows.some(row => selectedIds.has(rowId(row)));

  const handleSelectAll = () => {
    if (!onSelectionChange) return;
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(selectableRows.map(row => rowId(row))));
    }
  };

  const handleSelectRow = (row, e) => {
    e.stopPropagation();
    if (!onSelectionChange) return;
    const id = rowId(row);
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    onSelectionChange(next);
  };

  const densityClass = density !== 'default' ? `ui-table--${density}` : '';

  const renderRow = (row, idx) => {
    const id = rowId(row);
    const isSelected = selectedIds.has(id);
    return (
      <tr
        key={id ?? idx}
        className={`${onRowClick ? 'ui-table__row--clickable' : ''} ${isSelected ? 'ui-table__row--selected' : ''} ${rowClassName ? rowClassName(row, idx) : ''}`}
        onClick={(e) => onRowClick?.(row, idx, e)}
        onAuxClick={(e) => { if (e.button === 1) onRowClick?.(row, idx, e); }}
      >
        {selectable && (
          <td className="ui-table__checkbox-col">
            {isRowSelectable(row) && (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={(e) => handleSelectRow(row, e)}
                className="ui-table__checkbox"
              />
            )}
          </td>
        )}
        {columns.map((col, colIdx) => (
          <td
            key={col.key}
            className={`${col.align === 'right' ? 'ui-table--right' : ''} ${stickyFirstColumn && colIdx === 0 ? 'ui-table__sticky-cell' : ''}`}
          >
            {col.render ? col.render(row[col.key], row, idx) : row[col.key]}
          </td>
        ))}
      </tr>
    );
  };

  if (loading) {
    return (
      <div className={`ui-table-wrap ${className}`}>
        <table className={`ui-table ${densityClass} ${stickyHeader ? 'ui-table--sticky' : ''}`}>
          <thead>
            <tr>
              {selectable && <th className="ui-table__checkbox-col" />}
              {columns.map(c => <th key={c.key} style={c.width ? { width: c.width } : undefined}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 5 }).map((_, i) => (
              <tr key={i}>
                {selectable && <td className="ui-table__checkbox-col"><Skeleton variant="circle" width={16} height={16} /></td>}
                {columns.map(c => (
                  <td key={c.key}><Skeleton variant="text" width={`${50 + Math.random() * 40}%`} /></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (error && !data.length) {
    const msg = typeof error === 'string' ? error : (error?.message || 'Не удалось загрузить данные');
    return <ErrorState message={`Не удалось загрузить: ${msg}`} onRetry={onRetry} />;
  }

  if (!data.length) {
    return <EmptyState icon={emptyIcon} title={emptyTitle} description={emptyDescription} action={emptyAction} />;
  }

  return (
    <div className={`ui-table-wrap ${className}`}>
      {/* Desktop table */}
      <table className={`ui-table ${densityClass} ${stickyHeader ? 'ui-table--sticky' : ''} ${stickyFirstColumn ? 'ui-table--sticky-col' : ''} ${zebraStripe ? 'ui-table--zebra' : ''}`}>
        <thead>
          <tr>
            {selectable && (
              <th className="ui-table__checkbox-col">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                  onChange={handleSelectAll}
                  className="ui-table__checkbox"
                />
              </th>
            )}
            {columns.map((col, colIdx) => (
              <th
                key={col.key}
                className={`${col.align === 'right' ? 'ui-table--right' : ''} ${col.sortable ? 'ui-table--sortable' : ''} ${stickyFirstColumn && colIdx === 0 ? 'ui-table__sticky-cell' : ''}`}
                style={col.width ? { width: col.width } : undefined}
                onClick={() => handleSort(col)}
              >
                <span className="ui-table__th-content">
                  {col.label}
                  {col.sortable && sortKey === col.key && (
                    sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                  )}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(() => {
            if (!groupBy) return null;
            // Раскладываем строки по группам, сохраняя исходный порядок внутри
            const buckets = new Map();
            data.forEach((row) => {
              const g = groupBy(row) || { key: '_', label: '' };
              if (!buckets.has(g.key)) buckets.set(g.key, { ...g, rows: [] });
              buckets.get(g.key).rows.push(row);
            });
            const keys = groupOrder
              ? groupOrder.filter(k => buckets.has(k))
              : [...buckets.keys()];
            const colCount = columns.length + (selectable ? 1 : 0);
            return keys.map(k => {
              const g = buckets.get(k);
              return (
                <React.Fragment key={`g-${k}`}>
                  <tr className="ui-table__group">
                    <td colSpan={colCount}>
                      <span className="ui-table__group-label" style={g.color ? { color: g.color } : undefined}>
                        {g.label}
                      </span>
                      <span className="ui-table__group-count">{g.rows.length}</span>
                    </td>
                  </tr>
                  {g.rows.map((row, i) => renderRow(row, i))}
                </React.Fragment>
              );
            });
          })()}
          {!groupBy && data.map((row, idx) => renderRow(row, idx))}
        </tbody>
      </table>

      {/* Mobile cards */}
      <div className="ui-table-mobile">
        {data.map((row, idx) => {
          const isExpanded = expandedRows.has(idx);
          const id = rowId(row);
          const isSelected = selectable && selectedIds.has(id);
          return (
            <div key={id ?? idx} className={`ui-table-mobile__card ${isSelected ? 'ui-table-mobile__card--selected' : ''}`} onClick={() => onRowClick?.(row, idx)}>
              <div className="ui-table-mobile__main">
                {selectable && isRowSelectable(row) && (
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => handleSelectRow(row, e)}
                    className="ui-table__checkbox"
                  />
                )}
                <div className="ui-table-mobile__fields">
                  {priorityCols.map(col => (
                    <div key={col.key} className="ui-table-mobile__field">
                      <span className="ui-table-mobile__label">{col.label}</span>
                      <span className="ui-table-mobile__value">
                        {col.render ? col.render(row[col.key], row, idx) : row[col.key]}
                      </span>
                    </div>
                  ))}
                </div>
                {extraCols.length > 0 && (
                  <button
                    className={`ui-table-mobile__expand ${isExpanded ? 'ui-table-mobile__expand--open' : ''}`}
                    onClick={(e) => { e.stopPropagation(); toggleRow(idx); }}
                    aria-label="Подробнее"
                  >
                    <ChevronRight size={16} />
                  </button>
                )}
              </div>
              {isExpanded && extraCols.length > 0 && (
                <div className="ui-table-mobile__extra">
                  {extraCols.map(col => (
                    <div key={col.key} className="ui-table-mobile__field">
                      <span className="ui-table-mobile__label">{col.label}</span>
                      <span className="ui-table-mobile__value">
                        {col.render ? col.render(row[col.key], row, idx) : row[col.key]}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {footer && <div className="ui-table__footer">{footer}</div>}
    </div>
  );
}
